import { Twitter, Facebook, Link2, Share2, Linkedin, MessageCircle, Mail, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export function ShareButtons({ url, title, description, image, variant = 'default', compact = false, pick }) {
  // Support both variant="compact" and compact={true} props
  const isCompact = variant === 'compact' || compact;
  
  const shareUrl = url || (pick ? `https://primestatlab.com/pick/${pick.id}` : window.location.href);
  const shareTitle = title || (pick ? `${pick.player_name} - ${pick.prop_type}: ${pick.pick}` : document.title);
  // Clean the description of any SEO metadata text
  const cleanDescription = (text) => {
    if (!text) return '';
    return text
      .replace(/SEO TITLE/gi, '')
      .replace(/META DESCRIPTION/gi, '')
      .replace(/##?\s*\d*\.?\s*/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };
  const shareText = cleanDescription(description);

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
  };

  const shareToTwitter = () => {
    const tweetText = encodeURIComponent(shareTitle);
    const tweetUrl = encodeURIComponent(shareUrl);
    window.open(
      `https://twitter.com/intent/tweet?text=${tweetText}&url=${tweetUrl}`,
      '_blank',
      'width=550,height=420'
    );
  };

  const shareToFacebook = () => {
    const fbUrl = encodeURIComponent(shareUrl);
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${fbUrl}`,
      '_blank',
      'width=550,height=420'
    );
  };

  const shareToLinkedIn = () => {
    const linkedInUrl = encodeURIComponent(shareUrl);
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${linkedInUrl}`,
      '_blank',
      'width=550,height=420'
    );
  };

  const shareToReddit = () => {
    const redditTitle = encodeURIComponent(shareTitle);
    const redditUrl = encodeURIComponent(shareUrl);
    window.open(
      `https://reddit.com/submit?url=${redditUrl}&title=${redditTitle}`,
      '_blank',
      'width=550,height=420'
    );
  };

  const shareToWhatsApp = () => {
    const waText = encodeURIComponent(`${shareTitle}\n${shareUrl}`);
    window.open(
      `https://wa.me/?text=${waText}`,
      '_blank'
    );
  };

  const shareToTelegram = () => {
    const tgUrl = encodeURIComponent(shareUrl);
    const tgText = encodeURIComponent(shareTitle);
    window.open(
      `https://t.me/share/url?url=${tgUrl}&text=${tgText}`,
      '_blank'
    );
  };

  const shareToDiscord = () => {
    // Discord doesn't have a direct share URL, copy formatted text for easy pasting
    const discordText = `**${shareTitle}**\n${shareUrl}`;
    navigator.clipboard.writeText(discordText);
    toast.success('Copied for Discord! Paste in your channel.');
  };

  const shareToThreads = () => {
    // Threads doesn't have a direct share URL, but we can copy text for manual paste
    const threadsText = `${shareTitle}\n\n${shareUrl}`;
    navigator.clipboard.writeText(threadsText);
    toast.success('Copied for Threads! Open Threads and paste.');
  };

  const shareByEmail = () => {
    const subject = encodeURIComponent(shareTitle);
    const body = encodeURIComponent(`Check this out:\n\n${shareTitle}\n\n${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareToInstagram = () => {
    // Instagram doesn't support link sharing, copy text instead
    const igText = `${shareTitle}\n\nLink in bio: ${shareUrl}`;
    navigator.clipboard.writeText(igText);
    toast.success('Copied for Instagram! Paste in your caption.');
  };

  const shareToPinterest = () => {
    const pinUrl = encodeURIComponent(shareUrl);
    const pinDesc = encodeURIComponent(shareTitle);
    const pinImage = image ? encodeURIComponent(image) : '';
    window.open(
      `https://pinterest.com/pin/create/button/?url=${pinUrl}&description=${pinDesc}${pinImage ? `&media=${pinImage}` : ''}`,
      '_blank',
      'width=550,height=420'
    );
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  if (isCompact) {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={shareToTwitter}
          className="text-muted-foreground hover:text-[#1DA1F2]"
          title="Share on X/Twitter"
        >
          <Twitter size={18} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={shareToFacebook}
          className="text-muted-foreground hover:text-[#4267B2]"
          title="Share on Facebook"
        >
          <Facebook size={18} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={copyLink}
          className="text-muted-foreground hover:text-primary"
          title="Copy link"
        >
          <Link2 size={18} />
        </Button>
        {navigator.share && (
          <Button
            variant="ghost"
            size="sm"
            onClick={nativeShare}
            className="text-muted-foreground hover:text-primary"
            title="More sharing options"
          >
            <Share2 size={18} />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <span className="text-sm font-mono uppercase tracking-wider text-muted-foreground">
        Share This Analysis:
      </span>
      
      {/* Social Media Row */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={shareToTwitter}
          className="gap-2 hover:bg-[#1DA1F2] hover:text-white hover:border-[#1DA1F2]"
          data-testid="share-twitter"
        >
          <Twitter size={16} />
          X / Twitter
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={shareToFacebook}
          className="gap-2 hover:bg-[#4267B2] hover:text-white hover:border-[#4267B2]"
          data-testid="share-facebook"
        >
          <Facebook size={16} />
          Facebook
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={shareToReddit}
          className="gap-2 hover:bg-[#FF4500] hover:text-white hover:border-[#FF4500]"
          data-testid="share-reddit"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
          </svg>
          Reddit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={shareToLinkedIn}
          className="gap-2 hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]"
          data-testid="share-linkedin"
        >
          <Linkedin size={16} />
          LinkedIn
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={shareToThreads}
          className="gap-2 hover:bg-[#000000] hover:text-white hover:border-[#000000]"
          data-testid="share-threads"
        >
          <MessageCircle size={16} />
          Threads
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={shareToInstagram}
          className="gap-2 hover:bg-gradient-to-r hover:from-[#833AB4] hover:via-[#FD1D1D] hover:to-[#F77737] hover:text-white hover:border-[#833AB4]"
          data-testid="share-instagram"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
          Instagram
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={shareToPinterest}
          className="gap-2 hover:bg-[#E60023] hover:text-white hover:border-[#E60023]"
          data-testid="share-pinterest"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
          </svg>
          Pinterest
        </Button>
      </div>
      
      {/* Messaging Apps Row */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={shareToTelegram}
          className="gap-2 hover:bg-[#0088cc] hover:text-white hover:border-[#0088cc]"
          data-testid="share-telegram"
        >
          <Send size={16} />
          Telegram
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={shareToWhatsApp}
          className="gap-2 hover:bg-[#25D366] hover:text-white hover:border-[#25D366]"
          data-testid="share-whatsapp"
        >
          <MessageCircle size={16} />
          WhatsApp
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={shareToDiscord}
          className="gap-2 hover:bg-[#5865F2] hover:text-white hover:border-[#5865F2]"
          data-testid="share-discord"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
          </svg>
          Discord
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={shareByEmail}
          className="gap-2 hover:bg-gray-600 hover:text-white hover:border-gray-600"
          data-testid="share-email"
        >
          <Mail size={16} />
          Email
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={copyLink}
          className="gap-2"
          data-testid="share-copy-link"
        >
          <Link2 size={16} />
          Copy Link
        </Button>
        
        {/* Native Share (Mobile) */}
        {navigator.share && (
          <Button
            variant="outline"
            size="sm"
            onClick={nativeShare}
            className="gap-2 bg-primary/10 border-primary text-primary"
            data-testid="share-more"
          >
            <Share2 size={16} />
            More...
          </Button>
        )}
      </div>
      
      <p className="text-xs text-muted-foreground">
        Instagram, Threads & Discord: Text copied to clipboard for easy pasting
      </p>
    </div>
  );
}
